import { productStatus } from "@stackje/site-builder";
import type { BuilderApi, ShopApi, ShopIndex, ShopProductEntry } from "../../api/contract";
import { PRODUCT_SECTIONS, type ProductSection } from "../../lib/shop";
import { AreaError, Card, ConfirmDialog, EditorHead, SaveBar, StatusChip, SwitchRow, Tabs } from "../area";
import { ShopCustomizations } from "./Customizations";
import { DetailsTab } from "./DetailsTab";
import { DownloadsTab } from "./DownloadsTab";
import { SearchTab } from "./SearchTab";
import { useProductEditor, type ProductConfirm, type ProductEditorState } from "./useProductEditor";
import { VariantsTab } from "./VariantsTab";
import { ShopActivity, ShopFieldsSkeleton, ShopLoadError } from "./ShopLoading";
import { shopRequest } from "./shopRequest";

interface ProductEditorProps {
  api: ShopApi;
  builderApi?: BuilderApi;
  siteId: string;
  shop: ShopIndex;
  product: ShopProductEntry;
  language: string;
  reload: () => Promise<void>;
  onBack: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const CONFIRM_COPY: Record<ProductConfirm, { title: (name: string) => string; text: string; confirm: string }> = {
  available: { title: (name) => `Make “${name}” available?`, text: "It appears in your shop, and saved changes reach buyers straight away.", confirm: "Make available" },
  hidden: { title: () => "Hide this product?", text: "It will leave shop lists and search. Your product is kept.", confirm: "Hide product" },
  delete: { title: () => "Delete this product?", text: "It leaves the catalogue. Its private file versions and stock records are kept.", confirm: "Delete product" },
};

/** How many variants, files and customizations a product has, shown on those tabs once it has any. */
function tabCount(product: ShopProductEntry, id: ProductSection): { count?: number } {
  const count = id === "variants" ? (product.details.options.length ? product.variants.length : 0)
    : id === "downloads" ? product.details.files.length
    : id === "customizations" ? product.details.customizationSetIds.length + product.details.textFields.length
    : 0;
  return count ? { count } : {};
}

function ProductHistory({ editor }: { editor: ProductEditorState }) {
  const { api, siteId, product, language, history, setHistory, historyOpen, historyLoad, busy, dirty, work } = editor;
  if (!historyOpen) return null;
  if (!history) return <Card title="Product history">{historyLoad.error ? <ShopLoadError message={historyLoad.error} retry={historyLoad.retry} /> : <ShopFieldsSkeleton label="Loading product history" />}<button type="button" className="bw-btn bw-btn--text" onClick={editor.closeHistory}>Close history</button></Card>;
  const undone = new Set(history.flatMap((entry) => entry.undoes ?? []));
  const undoable = history.some((entry) => entry.before && !entry.undoes && !undone.has(entry.seq) && entry.action !== "availability");
  return <Card title="Product history">
    <p>Restore keeps current availability and never changes stock.</p>
    <ul className="bw-shop-history">
      {history.map((entry) => <li key={entry.seq}>
        <span>{entry.action.startsWith("stax:") ? "Stax text" : entry.action} · {new Date(entry.at).toLocaleString(language)}</span>
        {entry.after && <button type="button" className="bw-btn bw-btn--quiet" disabled={busy || dirty} onClick={() => void work(() => api.restoreProduct(siteId, product.id, product.rowVersion, entry.seq))}>{busy ? <ShopActivity announce={false} label="Restoring…" /> : "Restore"}</button>}
      </li>)}
    </ul>
    <div className="bw-actions">{undoable && <button type="button" className="bw-btn bw-btn--quiet" disabled={busy || dirty} onClick={() => void work(async () => { await api.undoProduct(siteId, product.id, product.rowVersion); setHistory(await shopRequest(signal => api.getProductHistory(siteId, product.id, signal))); }, false)}>{busy ? <ShopActivity announce={false} label="Working…" /> : "Undo last product change"}</button>}<button type="button" className="bw-btn bw-btn--text bw-area-link" onClick={editor.closeHistory}>Close history</button></div>
  </Card>;
}

function ProductSaveBar({ editor }: { editor: ProductEditorState }) {
  const { api, siteId, product, busy, dirty, saved, canUndo, waiting, save, discard, work } = editor;
  if (dirty) return <SaveBar state="dirty" message={waiting} busy={busy} onSave={() => void save()} onDiscard={discard} />;
  if (saved) return <SaveBar state="saved" busy={busy} {...(canUndo ? { onUndo: () => void work(() => api.undoProduct(siteId, product.id, product.rowVersion)) } : {})} />;
  return <SaveBar state="idle" message="Name, description and photos save as you go" />;
}

/** One product: its head (back, title, status, availability), its sections as tabs, and where its changes stand. */
export function ProductEditor({ api, builderApi, siteId, shop, product, language, reload, onBack, onDirtyChange }: ProductEditorProps) {
  const editor = useProductEditor({ api, siteId, shop, product, language, reload, onBack, ...(onDirtyChange ? { onDirtyChange } : {}) });
  const { section, changeSection, busy, dirty, confirm, setConfirm, error, setError } = editor;
  const available = product.availability === "available";
  const tabs = PRODUCT_SECTIONS.map((item) => ({ ...item, ...tabCount(product, item.id) }));
  return <div aria-busy={busy}>
    <EditorHead backLabel="Products" backDisabled={dirty || busy} onBack={onBack} title={product.details.name} status={<StatusChip status={productStatus(product, shop.stock)} />}
      aside={<SwitchRow title="Available in shop" hint={available ? "On: saved changes reach buyers straight away" : "Off: not in your shop, lists or search"} checked={available} disabled={busy || dirty} onChange={() => setConfirm(available ? "hidden" : "available")} />}
      menu={[
        { label: "History", onSelect: editor.openHistory },
        { label: "Delete product", tone: "danger", disabled: dirty || busy, onSelect: () => setConfirm("delete") },
      ]} />
    {busy && <div className="bw-shop-working"><ShopActivity label="Working…" /></div>}
    {confirm && <ConfirmDialog title={CONFIRM_COPY[confirm].title(product.details.name)} confirmLabel={busy ? "Working…" : CONFIRM_COPY[confirm].confirm} cancelLabel="Not now" tone={confirm === "delete" ? "danger" : "action"} busy={busy} onCancel={() => { if (!busy) setConfirm(null); }} onConfirm={() => void editor.confirmAction(confirm)}>
      {busy && <ShopActivity label="Working…" />}<p className="bw-area-dialog-lead">{CONFIRM_COPY[confirm].text}</p>
    </ConfirmDialog>}
    <div className="bw-area-head"><Tabs label="Product sections" items={tabs} current={section} onSelect={changeSection} /></div>
    <div className="bw-area-stack">
      {section === "details" && <DetailsTab editor={editor} builderApi={builderApi} />}
      {section === "variants" && <VariantsTab editor={editor} />}
      {section === "downloads" && <DownloadsTab editor={editor} />}
      {section === "customizations" && <ShopCustomizations api={api} siteId={siteId} shop={shop} product={product} language={language} reload={reload} onDirtyChange={editor.setCustomDirty} />}
      {section === "search" && <SearchTab editor={editor} />}
      <ProductHistory editor={editor} />
    </div>
    {error && <AreaError message={error} onDismiss={() => setError(null)} />}
    {section !== "customizations" && <ProductSaveBar editor={editor} />}
  </div>;
}
